import { RefObject, useEffect, useRef } from "react";
import type { CoordinateSet } from "@/map/types/map";
import { collectHeatmapPoints } from "@/map/utils/heatmap";
import { HEATMAP_STYLE } from "@/map/utils/mapConstants";
import { createHeatmapOverlay, type HeatmapOverlay } from "@/map/utils/heatmap-overlay";

interface UseMapHeatmapProps {
  mapRef: RefObject<google.maps.Map | null>;
  mapsRef: RefObject<typeof google.maps | null>;
  basicCoordinates: CoordinateSet[];
}

/**
 * Render GPS trail points (sets with `markerType: 'gps'`) as a heatmap.
 *
 * Google removed the built-in `visualization.HeatmapLayer` in Maps JS v3.65, so
 * we draw a `simpleheat` canvas overlay instead (see `createHeatmapOverlay`).
 * Mirrors `useMapPaths`: rebuilds whenever the coordinates change and tears the
 * overlay down on cleanup. No-ops until the map is ready or when there are no
 * GPS points.
 */
export const useMapHeatmap = ({
  mapRef,
  mapsRef,
  basicCoordinates,
}: UseMapHeatmapProps) => {
  const overlayRef = useRef<HeatmapOverlay | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    // Tear down the previous overlay before rebuilding.
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }

    const points = collectHeatmapPoints(basicCoordinates);
    if (points.length === 0) return;

    try {
      const overlay = createHeatmapOverlay(maps, points, HEATMAP_STYLE);
      overlay.setMap(map);
      overlayRef.current = overlay;
    } catch (e) {
      console.warn("[useMapHeatmap] failed to create heatmap overlay", e);
      return;
    }

    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [basicCoordinates, mapRef.current, mapsRef.current]);
};
